import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { usePermissions } from './PermissionContext';
import { listBranches, listWarehouses } from '../api/orgData';
import type { Branch, UUID } from '../types/domain';

/**
 * Active-branch selection. This is an application UX concern, not a
 * business-policy invention: several vertical-slice-1 actions
 * (creating a PR, creating a GR) require a specific branch_id, but a user
 * with a tenant-wide role grant (branch_id = null in their user_roles row —
 * e.g. TENANT_OWNER, see migration 0032) can act in ANY branch, so the app
 * needs to know which one they currently mean. A user whose only role
 * grant is scoped to one branch (e.g. PURCHASING at Branch A) defaults to
 * that branch and — since fn_check_permission would deny them anyway in any
 * other branch — is not offered a choice.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */

interface BranchState {
  branches: Branch[];
  activeBranchId: UUID | null;
  setActiveBranchId: (branchId: UUID) => void;
  loading: boolean;
  /** Warehouse IDs belonging to the active branch — for the handful of
   * tables (stock_balances, transfers) that carry warehouse_id but no
   * direct branch_id column. Empty while warehouses haven't resolved yet;
   * callers should treat an empty array + loading as "not ready" rather
   * than "branch has no warehouses". */
  activeBranchWarehouseIds: UUID[];
}

export const BranchContext = createContext<BranchState | undefined>(undefined);

export function BranchProvider({ children }: { children: ReactNode }) {
  const { context } = usePermissions();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeBranchId, setActiveBranchIdState] = useState<UUID | null>(null);
  const [activeBranchWarehouseIds, setActiveBranchWarehouseIds] = useState<UUID[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!context || !context.authenticated) {
      setBranches([]);
      setActiveBranchIdState(null);
      setActiveBranchWarehouseIds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listBranches()
      .then((list) => {
        if (cancelled) return;
        setBranches(list);
        // Default: the first branch-specific role assignment, if any;
        // otherwise (only tenant-wide grants) the first branch in the list.
        const scopedRole = context.roles.find((r) => r.branch_id !== null);
        const defaultBranch = scopedRole?.branch_id ?? list[0]?.branch_id ?? null;
        setActiveBranchIdState(defaultBranch);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  // Re-resolves whenever the active branch changes — this is the single
  // dependency every branch-scoped list page keys its warehouse-based
  // queries (stock_balances/transfers) off of.
  useEffect(() => {
    let cancelled = false;
    if (!activeBranchId) {
      setActiveBranchWarehouseIds([]);
      return;
    }
    listWarehouses(activeBranchId).then((rows) => {
      if (!cancelled) setActiveBranchWarehouseIds(rows.map((w) => w.warehouse_id));
    });
    return () => {
      cancelled = true;
    };
  }, [activeBranchId]);

  const setActiveBranchId = (branchId: UUID) => {
    setActiveBranchIdState(branchId);
  };

  const value = useMemo<BranchState>(
    () => ({ branches, activeBranchId, setActiveBranchId, loading, activeBranchWarehouseIds }),
    [branches, activeBranchId, loading, activeBranchWarehouseIds]
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch(): BranchState {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    throw new Error('useBranch() must be called within <BranchProvider>');
  }
  return ctx;
}
